/**
 * AssetManager — centralized asset loading with progress UI.
 *
 * Provides:
 *   1. A loading screen overlay (AdvancedDynamicTexture) styled to match
 *      the game's gothic steampunk aesthetic.
 *   2. glTF model loading via loadAssetContainerAsync with a per-scene
 *      template cache so repeated loads of the same path hit the cache.
 *      In-flight requests for the same path are de-duplicated.
 *   3. Instantiation helpers that clone cached templates into the scene
 *      so multi-instance assets (e.g. many pressure plates using the same
 *      model) only download once.
 *   4. Manifest-aware batch loading for pre-warming the cache and
 *      reporting aggregate progress to the loading screen.
 *   5. A module-level singleton (`getAssetManager()`) so any class can
 *      reach the manager without threading references through constructors.
 *
 * Key invariant: cached containers are NEVER automatically added to their
 * scene. Call `instantiateModel` to get a scene-ready clone, or use
 * `loadModel` if you need the raw template (e.g. to inspect metadata).
 *
 * Usage:
 *   const assetManager = getAssetManager();
 *   const hide = assetManager.showLoadingScreen(scene, "The Redwood Scrapyard");
 *   const instance = await assetManager.instantiateModel(scene, "/assets/models/minna.glb");
 *   hide.hide();
 */

import { Scene } from "@babylonjs/core/scene";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Node } from "@babylonjs/core/node";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
// eslint-disable-next-line deprecation/deprecation
import { loadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader";
import { MeshoptCompression } from "@babylonjs/core/Meshes/Compression/meshoptCompression";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import type { AssetManifest, LoadedManifest } from "../levels/AssetManifest";
import { configureResponsiveFullscreenUI } from "../ui/guiScaling";

// Serve the meshopt decoder from our own origin instead of Babylon's
// default CDN (https://cdn.babylonjs.com/meshopt_decoder.js). Some level
// assets (e.g. conservatory_boiler_sundew.glb) use EXT_meshopt_compression,
// and with the CDN default a player without access to that host cannot
// load Level 2 at all — the glTF loader hard-fails the whole scene setup.
// The decoder is vendored from the `meshoptimizer` package (v0.24, MIT).
MeshoptCompression.Configuration = {
  decoder: { url: "/vendor/meshopt_decoder.js" },
};

// ----- Constants matching the game's gothic steampunk palette -----
const BG_COLOR = "rgba(5, 3, 13, 0.95)";
const BRASS = "#c49b3c";
const BRASS_DIM = "rgba(196, 155, 60, 0.25)";
const TEXT_COLOR = "#d4c5a9";
const FONT_FAMILY = "Georgia, 'Times New Roman', serif";

/** Handle returned by showLoadingScreen — call to dismiss and dispose. */
export interface LoadingScreenHandle {
  /** Update the progress bar (0..1). */
  setProgress(fraction: number): void;
  /** Update the status text below the bar. */
  setStatus(text: string): void;
  /** Hide and dispose the loading screen GUI. */
  hide(): void;
}

/**
 * Result of instantiating a cached model into a scene. The root nodes and
 * descendant meshes are fresh clones owned by the scene — disposing them
 * is safe and does not affect the cached template.
 */
export interface InstantiatedModel {
  /** Cloned root nodes (typically a single glTF `__root__` TransformNode). */
  rootNodes: TransformNode[];
  /** Flattened list of every mesh in the instantiated hierarchy. */
  meshes: AbstractMesh[];
  /**
   * Animation clips retargeted to this instance's clones (empty for static
   * props). Their play state is whatever instantiateModelsToScene left.
   */
  animationGroups: AnimationGroup[];
  /** Dispose all cloned nodes. Safe to call multiple times. */
  dispose(): void;
}

export interface InstantiateModelOptions {
  cloneMaterials?: boolean;
  doNotInstantiate?: boolean | ((node: Node) => boolean);
}

export class AssetManager {
  /**
   * Scene-scoped template cache. Keys are scenes so entries vanish
   * automatically when a scene is disposed and garbage-collected. Values
   * store promises so concurrent callers for the same path share a single
   * in-flight request.
   */
  private cache: WeakMap<Scene, Map<string, Promise<AssetContainer>>> = new WeakMap();

  // ------------------------------------------------------------------
  // Model loading
  // ------------------------------------------------------------------

  /**
   * Get (or create) the per-scene template map. The map stores promises so
   * concurrent callers share a single in-flight load for the same path.
   */
  private getSceneMap(scene: Scene): Map<string, Promise<AssetContainer>> {
    let map = this.cache.get(scene);
    if (!map) {
      map = new Map();
      this.cache.set(scene, map);
    }
    return map;
  }

  /**
   * Load a glTF/glb model as a cached template and return the raw
   * `AssetContainer`. The container is NOT added to the scene — use
   * {@link instantiateModel} to spawn a scene-ready clone.
   *
   * @param scene  The scene that owns the assets.
   * @param path   URL or path to the glTF/glb file.
   * @param onProgress  Optional progress callback (called with 0..1).
   */
  async loadModel(
    scene: Scene,
    path: string,
    onProgress?: (fraction: number) => void,
  ): Promise<AssetContainer> {
    const map = this.getSceneMap(scene);
    const cached = map.get(path);
    if (cached) {
      onProgress?.(1);
      return cached;
    }

    // loadAssetContainerAsync doesn't expose fine-grained progress natively,
    // but we can signal start/end for the loading screen.
    onProgress?.(0);

    // Store the promise immediately so concurrent callers share it.
    const promise = loadAssetContainerAsync(path, scene).then(
      (container) => {
        onProgress?.(1);
        return container;
      },
      (err) => {
        // Remove the failed entry so a retry has a chance to succeed.
        map.delete(path);
        throw err;
      },
    );
    map.set(path, promise);
    return promise;
  }

  /**
   * Load multiple models in parallel, reporting aggregate progress.
   * Each load hits the cache independently, so duplicate paths only
   * trigger a single download.
   */
  async loadModels(
    scene: Scene,
    paths: string[],
    onProgress?: (fraction: number) => void,
  ): Promise<Map<string, AssetContainer>> {
    const results = new Map<string, AssetContainer>();
    if (paths.length === 0) {
      onProgress?.(1);
      return results;
    }

    const perModel = new Float32Array(paths.length); // individual progress 0..1

    const report = (): void => {
      let sum = 0;
      for (let i = 0; i < perModel.length; i++) sum += perModel[i];
      onProgress?.(sum / paths.length);
    };

    await Promise.all(
      paths.map(async (path, idx) => {
        const container = await this.loadModel(scene, path, (frac) => {
          perModel[idx] = frac;
          report();
        });
        results.set(path, container);
      }),
    );

    onProgress?.(1);
    return results;
  }

  /**
   * Load every model path declared by an {@link AssetManifest} into the
   * scene's cache. Returns a {@link LoadedManifest} with containers grouped
   * by role (environment / characters / interactives) plus a flat `byPath`
   * lookup. Audio is intentionally ignored — AudioManager handles that.
   */
  async loadManifest(
    scene: Scene,
    manifest: AssetManifest,
    onProgress?: (fraction: number) => void,
  ): Promise<LoadedManifest> {
    // Build a unique path list so we count progress correctly even if a
    // manifest accidentally lists the same asset in two buckets.
    const unique = new Set<string>();
    if (manifest.environment) unique.add(manifest.environment);
    for (const p of manifest.characters) unique.add(p);
    for (const p of manifest.interactives) unique.add(p);
    const paths = Array.from(unique);

    const byPath = await this.loadModels(scene, paths, onProgress);

    const characters = new Map<string, AssetContainer>();
    for (const p of manifest.characters) {
      const c = byPath.get(p);
      if (c) characters.set(p, c);
    }
    const interactives = new Map<string, AssetContainer>();
    for (const p of manifest.interactives) {
      const c = byPath.get(p);
      if (c) interactives.set(p, c);
    }
    const environment = manifest.environment ? byPath.get(manifest.environment) ?? null : null;

    return { environment, characters, interactives, byPath };
  }

  /**
   * Load (or hit the cache for) a model, then instantiate fresh clones of
   * its root nodes into the target scene. Multiple calls with the same
   * path produce independent clones.
   *
   * The returned {@link InstantiatedModel} includes a flat `meshes` list
   * so callers can iterate every cloned mesh without walking the node tree
   * manually.
   */
  async instantiateModel(
    scene: Scene,
    path: string,
    onProgress?: (fraction: number) => void,
    options: InstantiateModelOptions = {},
  ): Promise<InstantiatedModel> {
    const container = await this.loadModel(scene, path, onProgress);

    // Clone materials so per-instance material edits (e.g. emissive flash)
    // don't bleed across instances sharing the template.
    const entries = container.instantiateModelsToScene(
      undefined,
      options.cloneMaterials ?? true,
      options.doNotInstantiate === undefined
        ? undefined
        : { doNotInstantiate: options.doNotInstantiate },
    );

    const rootNodes: TransformNode[] = [];
    const meshes: AbstractMesh[] = [];
    for (const node of entries.rootNodes) {
      if (node instanceof TransformNode) {
        rootNodes.push(node);
      }
      collectMeshesFromNode(node, meshes);
    }

    return {
      rootNodes,
      meshes,
      animationGroups: entries.animationGroups,
      dispose(): void {
        for (const node of entries.rootNodes) {
          // Use dispose with recursive = true to tear down descendants.
          // Shared instances borrow the cached container's material/texture
          // resources. Retiring one instance must not destroy its siblings.
          (node as Node).dispose(false, options.cloneMaterials ?? true);
        }
        entries.rootNodes.length = 0;
        entries.animationGroups.forEach((g) => g.dispose());
        entries.skeletons.forEach((s) => s.dispose());
      },
    };
  }

  async instantiateSharedModel(
    scene: Scene,
    path: string,
    onProgress?: (fraction: number) => void,
  ): Promise<InstantiatedModel> {
    return this.instantiateModel(scene, path, onProgress, {
      cloneMaterials: false,
      // Templates can be prewarmed before level lights exist. GPU instances
      // inherit that off-scene source's stale light/visibility state. Mesh
      // clones share geometry/materials while owning scene light exclusions.
      doNotInstantiate: true,
    });
  }

  // ------------------------------------------------------------------
  // Loading screen UI
  // ------------------------------------------------------------------

  /**
   * Show a loading screen overlay on the given scene.
   *
   * Returns a handle to update progress and dismiss the screen.
   * The overlay uses a fullscreen AdvancedDynamicTexture that sits
   * on top of whatever is currently rendering.
   */
  showLoadingScreen(scene: Scene, levelName: string): LoadingScreenHandle {
    const gui = AdvancedDynamicTexture.CreateFullscreenUI("loadingUI", true, scene);
    const disposeGuiScaling = configureResponsiveFullscreenUI(gui, scene);

    // Full-screen dark backdrop
    const backdrop = new Rectangle("loadingBackdrop");
    backdrop.width = 1;
    backdrop.height = 1;
    backdrop.thickness = 0;
    backdrop.background = BG_COLOR;
    backdrop.isHitTestVisible = false;
    gui.addControl(backdrop);

    // Center container
    const panel = new StackPanel("loadingPanel");
    panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    panel.isHitTestVisible = false;
    gui.addControl(panel);

    // Level name
    const levelText = new TextBlock("loadingLevel");
    levelText.text = levelName;
    levelText.color = BRASS;
    levelText.fontSize = 42;
    levelText.fontFamily = FONT_FAMILY;
    levelText.fontWeight = "bold";
    levelText.height = "60px";
    levelText.shadowColor = "rgba(0,0,0,0.6)";
    levelText.shadowBlur = 6;
    panel.addControl(levelText);

    // "Loading..." label
    const loadingLabel = new TextBlock("loadingText");
    loadingLabel.text = "Loading...";
    loadingLabel.color = TEXT_COLOR;
    loadingLabel.fontSize = 24;
    loadingLabel.fontFamily = FONT_FAMILY;
    loadingLabel.fontStyle = "italic";
    loadingLabel.height = "40px";
    panel.addControl(loadingLabel);

    // Progress bar track
    const barTrack = new Rectangle("barTrack");
    barTrack.width = "380px";
    barTrack.height = "22px";
    barTrack.cornerRadius = 11;
    barTrack.thickness = 1.5;
    barTrack.color = BRASS;
    barTrack.background = BRASS_DIM;
    barTrack.isHitTestVisible = false;
    panel.addControl(barTrack);

    // Progress bar fill
    const barFill = new Rectangle("barFill");
    barFill.width = 0;
    barFill.height = "18px";
    barFill.cornerRadius = 9;
    barFill.thickness = 0;
    barFill.background = BRASS;
    barFill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    barFill.left = "2px";
    barFill.isHitTestVisible = false;
    barTrack.addControl(barFill);

    // Status text (below bar)
    const statusText = new TextBlock("loadingStatus");
    statusText.text = "";
    statusText.color = TEXT_COLOR;
    statusText.fontSize = 16;
    statusText.fontFamily = FONT_FAMILY;
    statusText.height = "30px";
    statusText.alpha = 0.7;
    panel.addControl(statusText);

    // Track width for progress calculations
    const trackWidth = 376; // 380 - 4px padding

    return {
      setProgress(fraction: number) {
        const clamped = Math.max(0, Math.min(1, fraction));
        barFill.width = `${Math.round(clamped * trackWidth)}px`;
      },

      setStatus(text: string) {
        statusText.text = text;
      },

      hide() {
        disposeGuiScaling();
        gui.dispose();
      },
    };
  }

  // ------------------------------------------------------------------
  // Cache management
  // ------------------------------------------------------------------

  /** Check whether a model is already cached for a specific scene. */
  isCached(scene: Scene, path: string): boolean {
    return this.cache.get(scene)?.has(path) ?? false;
  }

  /**
   * Evict a single model from the cache for a specific scene and dispose
   * its container. Useful for hot-reload or when a level explicitly wants
   * to drop a heavy asset.
   */
  async evict(scene: Scene, path: string): Promise<void> {
    const map = this.cache.get(scene);
    if (!map) return;
    const entry = map.get(path);
    if (entry) {
      map.delete(path);
      try {
        const container = await entry;
        container.dispose();
      } catch {
        // Already cleaned up on failure in loadModel.
      }
    }
  }

  /**
   * Dispose all cached containers for a specific scene and clear that
   * scene's cache entry. Called automatically when the scene itself is
   * disposed (the WeakMap entry is then GC'd), but can be invoked
   * explicitly for deterministic cleanup.
   */
  async disposeScene(scene: Scene): Promise<void> {
    const map = this.cache.get(scene);
    if (!map) return;
    this.cache.delete(scene);
    for (const entry of map.values()) {
      try {
        const container = await entry;
        container.dispose();
      } catch {
        // Already cleaned up on failure.
      }
    }
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Walk a node hierarchy and collect every `AbstractMesh`. Used to
 * flatten instantiated models for callers that want to register
 * materials/shadows/etc. on each cloned mesh.
 */
function collectMeshesFromNode(node: Node, out: AbstractMesh[]): void {
  if (node instanceof AbstractMesh) out.push(node);
  for (const child of node.getChildren()) {
    collectMeshesFromNode(child, out);
  }
}

// ─── Singleton access ───────────────────────────────────────────────

let _singleton: AssetManager | null = null;

/**
 * Get the shared AssetManager singleton. Lazily created on first access
 * so tests and headless environments don't pay any initialization cost
 * until something actually loads an asset.
 */
export function getAssetManager(): AssetManager {
  if (!_singleton) {
    _singleton = new AssetManager();
  }
  return _singleton;
}

/**
 * Replace the singleton (test support). Primarily useful when a test
 * wants a fresh manager without cached entries from previous runs.
 */
export function setAssetManager(manager: AssetManager | null): void {
  _singleton = manager;
}
